import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { BadgeCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import CertificateActions from "../components/learning/CertificateActions";
import { api, formatApiError } from "../lib/api";
import { certImage, useLearning } from "../lib/learning";
import { fmtDate } from "../lib/format";
import { inputCls } from "../components/forms/Fields";
import NotFound from "./NotFound";

function RenameForm({ cert, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(cert.recipient_name);
  if (!open) return <button onClick={() => setOpen(true)} data-testid="cert-edit-name-btn" className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-navy"><Pencil size={13} /> Edit name on certificate</button>;
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.patch(`/portal/certificates/${cert.id}`, { recipient_name: name });
      toast.success("Name updated");
      setOpen(false);
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <form onSubmit={save} className="flex flex-wrap gap-2" data-testid="cert-rename-form">
      <input required minLength={2} maxLength={80} value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} max-w-xs`} data-testid="cert-name-input" />
      <button data-testid="cert-name-save-btn" className="rounded-full bg-navy px-5 text-sm font-semibold text-white">Save</button>
    </form>
  );
}

export default function CertificatePage() {
  const { id } = useParams();
  const [cert, setCert] = useState(null);
  const [missing, setMissing] = useState(false);
  const { certificates, reload } = useLearning();
  useEffect(() => { api.get(`/certificates/${id}`).then((r) => setCert(r.data)).catch(() => setMissing(true)); }, [id]);

  if (missing) return <NotFound />;
  if (!cert) return <div className="min-h-[70vh] bg-navy" data-testid="cert-loading" />;
  const isOwner = Object.values(certificates).some((c) => c.id === cert.id);

  return (
    <>
      <Seo title={`${cert.recipient_name} completed ${cert.course_title}`} description={`Verified Quantora Learning certificate ${cert.id}.`} path={`/certificates/${cert.id}`} image={certImage(cert.id, "og", cert.version)} noindex />
      <section className="grain relative bg-navy pb-24 pt-14 text-white">
        <div className="container-q relative">
          <p className="inline-flex items-center gap-2 rounded-full border border-mint/40 bg-mint/10 px-4 py-1.5 text-xs font-semibold text-mint" data-testid="cert-verified-badge"><BadgeCheck size={15} /> Verified certificate · {cert.id}</p>
          <h1 className="mt-5 text-4xl font-bold sm:text-5xl" data-testid="cert-title">{cert.recipient_name}</h1>
          <p className="mt-3 text-slate-300">completed <Link to={`/learn/${cert.course_slug}`} className="font-semibold text-mint hover:underline" data-testid="cert-course-link">{cert.course_title}</Link> · {cert.course_level} · {cert.lesson_count} lessons · issued {fmtDate(cert.issued_at)}</p>
          <div className="mt-10 overflow-hidden rounded-2xl border border-gold/30 shadow-2xl">
            <img src={certImage(cert.id, "full", cert.version)} alt={`Quantora certificate for ${cert.recipient_name}`} className="w-full" data-testid="cert-image" />
          </div>
        </div>
      </section>
      <section className="bg-white py-14">
        <div className="container-q flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h2 className="text-lg font-bold text-navy">{isOwner ? "Share your achievement" : "About this credential"}</h2>
            <p className="mt-1 max-w-xl text-sm text-slate-600">{isOwner ? "Add it to the Licenses & certifications section of your LinkedIn profile, or share a post. The link verifies the certificate publicly." : `This certificate confirms that ${cert.recipient_name} completed every lesson of this Quantora Learning course.`}</p>
            {isOwner && <div className="mt-3"><RenameForm cert={cert} onSaved={(c) => { setCert(c); reload(); }} /></div>}
          </div>
          {isOwner ? <CertificateActions cert={cert} /> : <Link to={`/learn/${cert.course_slug}`} data-testid="cert-take-course-btn" className="rounded-full bg-mint px-6 py-3.5 text-sm font-semibold text-navy">Take this free course</Link>}
        </div>
        <p className="container-q mt-10 text-xs text-slate-500">Quantora Learning certificates recognise completion of educational content. They are not professional, academic or regulatory qualifications.</p>
      </section>
    </>
  );
}
